// src/pages/SendNotifications.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';

import { useAuth } from '../contexts/AuthContext';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon, InformationCircleIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';

const SendNotifications = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState(''); // New state for email subject
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [notificationType, setNotificationType] = useState('email');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [apiCallInfo, setApiCallInfo] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    // Filter customers based on notification type
    if (notificationType === 'sms') {
      // For SMS, only show customers with SMS consent and not opted out
      setFilteredCustomers(customers.filter(customer => 
        customer.smsConsent && !customer.smsOptOut && customer.phone
      ));
      
      // Remove any selected customers who don't have SMS consent
      setSelectedCustomers(prev => 
        prev.filter(id => 
          customers.find(c => c._id === id && c.smsConsent && !c.smsOptOut && c.phone)
        )
      );
    } else {
      // For email, show all customers
      setFilteredCustomers(customers);
    }
  }, [notificationType, customers]);

  useEffect(() => {
    // Check for customer IDs in URL parameters
    const searchParams = new URLSearchParams(location.search);
    const customerIds = searchParams.get('customers');
    const type = searchParams.get('type');
    
    if (customerIds) {
      // If multiple IDs are comma-separated
      const idsArray = customerIds.split(',');
      setSelectedCustomers(idsArray);
    }
    
    // Set notification type if specified in URL
    if (type === 'sms') {
      setNotificationType('sms');
    }
  }, [location.search]);

  const fetchCustomers = async () => {
    try {
      const response = await axiosInstance.get('/customers');
      setCustomers(response.data);
      
      // Initialize filtered customers based on current notification type
      if (notificationType === 'sms') {
        setFilteredCustomers(response.data.filter(customer => 
          customer.smsConsent && !customer.smsOptOut && customer.phone
        ));
      } else {
        setFilteredCustomers(response.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError(
        `Failed to fetch customers. ${error.response?.data?.message || 'Please try again.'}`
      );
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    setSelectedCustomers(
      selectedCustomers.length === filteredCustomers.length
        ? []
        : filteredCustomers.map((customer) => customer._id)
    );
  };

  const handleSendNotification = async () => {
    if (selectedCustomers.length === 0) {
      setError('Please select at least one customer');
      return;
    }

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (notificationType === 'email' && !subject.trim()) {
      setError('Please enter a subject for the email');
      return;
    }

    try {
      setSending(true);
      setError('');
      setApiCallInfo(null);
      
      const selectedCustomerData = customers.filter(c => 
        selectedCustomers.includes(c._id)
      );

      // Enhanced debugging before sending
      console.log('Bulk notification - Preparation:', {
        type: notificationType,
        recipientCount: selectedCustomerData.length,
        messageLength: message.length,
        messagePreview: message.substring(0, 30) + (message.length > 30 ? '...' : ''),
        hasSubject: notificationType === 'email' ? !!subject.trim() : 'N/A',
        timestamp: new Date().toISOString()
      });

      // Filter out customers without phone numbers for SMS
      if (notificationType === 'sms') {
        const invalidCustomers = selectedCustomerData.filter(c => !c.phone || !c.smsConsent || c.smsOptOut);
        if (invalidCustomers.length > 0) {
          console.warn('Bulk notification - Invalid SMS recipients:', {
            count: invalidCustomers.length,
            details: invalidCustomers.map(c => ({
              id: c._id,
              name: c.name,
              hasPhone: !!c.phone,
              hasConsent: c.smsConsent,
              isOptedOut: c.smsOptOut
            }))
          });
        }
      }

      try {
        const response = await axiosInstance.post('/notifications/send', {
          customers: selectedCustomerData,
          message,
          subject, // Include subject for emails
          type: notificationType,
          businessInfo: {
            name: user.name,
            phoneNumber: user.phoneNumber
          }
        });

        // Enhanced debugging after sending
        console.log('Bulk notification - Success response:', {
          type: notificationType,
          totalRecipients: selectedCustomerData.length,
          successCount: response.data.results.filter(r => r.status === 'sent').length,
          failureCount: response.data.results.filter(r => r.status === 'failed').length,
          apiResponse: response.data,
          timestamp: new Date().toISOString()
        });

        setResult({
          success: true,
          message: 'Notifications sent successfully!',
          details: response.data.results
        });

        // Show API call information
        setApiCallInfo({
          totalRecipients: selectedCustomerData.length,
          apiCalls: notificationType === 'email' ? 1 : selectedCustomerData.length,
          type: notificationType,
          successCount: response.data.results.filter(r => r.status === 'sent').length,
          failureCount: response.data.results.filter(r => r.status === 'failed').length
        });

        // Clear form after successful send
        if (response.data.results.some(r => r.status === 'sent')) {
          setMessage('');
          setSubject('');
          setSelectedCustomers([]);
        }
      } catch (error) {
        console.error('Bulk notification - Error details:', {
          type: notificationType,
          errorStatus: error.response?.status,
          errorMessage: error.response?.data?.message || error.message,
          errorStack: error.stack,
          details: error.response?.data || 'No additional details',
          timestamp: new Date().toISOString()
        });

        let errorMessage = 'Failed to send notifications. Please try again.';
        
        // Handle specific error cases
        if (error.response?.status === 403) {
          // Check if we can do a partial send (API returned canSendPartial flag)
          if (error.response?.data?.canSendPartial && error.response?.data?.partialCount > 0) {
            const partialCount = error.response.data.partialCount;
            const totalCount = selectedCustomerData.length;
            
            // Ask user if they want to proceed with partial send
            if (window.confirm(
              `You can only send to ${partialCount} of ${totalCount} recipients due to your current usage limits. Would you like to proceed with a partial send?`
            )) {
              console.log('User opted to proceed with partial send');
              
              // Take only the first partialCount recipients
              const partialCustomers = selectedCustomerData.slice(0, partialCount);
              
              try {
                // Make the partial send request
                setSending(true);
                const partialResponse = await axiosInstance.post('/notifications/send', {
                  customers: partialCustomers,
                  message,
                  subject,
                  type: notificationType,
                  businessInfo: {
                    name: user.name,
                    phoneNumber: user.phoneNumber
                  }
                });
                
                console.log('Partial send successful:', partialResponse.data);
                
                setResult({
                  success: true,
                  message: `Sent to ${partialCount} recipients (out of ${totalCount} selected) due to usage limits.`,
                  details: partialResponse.data.results,
                  wasPartialSend: true
                });
                
                // Show API call information for partial send
                setApiCallInfo({
                  totalRecipients: partialCount,
                  apiCalls: notificationType === 'email' ? 1 : partialCount,
                  type: notificationType,
                  successCount: partialResponse.data.results.filter(r => r.status === 'sent').length,
                  failureCount: partialResponse.data.results.filter(r => r.status === 'failed').length,
                  wasPartialSend: true,
                  originalCount: totalCount
                });
                
                return; // Exit early to avoid setting error state
              } catch (partialError) {
                console.error('Error during partial send:', partialError);
                errorMessage = 'Partial send failed. Please try again later.';
              }
            } else {
              console.log('User declined partial send');
              errorMessage = 'Send cancelled. You can try again with fewer recipients or wait for your usage limit to reset.';
            }
          } else {
            errorMessage = 'You have reached the maximum number of notifications for your subscription tier. Please upgrade your plan to send more.';
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setResult({
          success: false,
          message: errorMessage,
          error: error.response?.data?.message || error.message
        });
      }
    } catch (outerError) {
      console.error('Unexpected error in send notification flow:', outerError);
      setResult({
        success: false,
        message: 'An unexpected error occurred. Please try again.',
        error: outerError.message
      });
    } finally {
      setSending(false);
    }
  };

  // Helper function to format phone numbers
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digits
    const cleaned = phoneNumber.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumber;
  };

  const handleReturnToCustomers = () => {
    navigate('/customers?tab=manage&action=notification&status=success');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900 text-text">
      
      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header with glass effect and glow */}
          <div className="relative mb-8">
            <div className="absolute inset-0 blur-[120px] bg-gradient-to-r from-blue-500/20 to-purple-600/20 transform -translate-y-4"></div>
            <div className="relative flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-extrabold text-white">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500">
                    Send Notifications
                  </span>
                </h1>
                <p className="mt-2 text-gray-400 max-w-2xl">
                  Send personalized communications to your customers via SMS or email
                </p>
              </div>
              <button
                onClick={() => navigate('/customers?tab=manage')}
                className="px-4 py-2 bg-gray-800/80 backdrop-blur-sm text-white rounded-lg hover:bg-gray-700 transition-all duration-300 flex items-center border border-gray-700/50 shadow-lg"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Customers
              </button>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-md shadow-xl rounded-xl p-8 border border-gray-700/70">
            {/* Notification Type Selection with glass morphism */}
            <div className="mb-8">
              <label className="block text-lg font-medium text-blue-300 mb-4">Notification Type</label>
              <div className="flex flex-wrap md:flex-nowrap gap-4 w-full">
                <button
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
                    notificationType === 'email'
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg shadow-blue-600/20 transform hover:scale-[1.02]'
                      : 'bg-gray-700/70 text-gray-300 hover:bg-gray-600/80 border border-gray-600/50'
                  }`}
                  onClick={() => setNotificationType('email')}
                >
                  <div className="flex items-center justify-center w-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8L10.8906 13.2604C11.5624 13.7083 12.4376 13.7083 13.1094 13.2604L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z" />
                    </svg>
                    Email
                  </div>
                </button>
                <button
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
                    notificationType === 'sms'
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg shadow-blue-600/20 transform hover:scale-[1.02]'
                      : 'bg-gray-700/70 text-gray-300 hover:bg-gray-600/80 border border-gray-600/50'
                  }`}
                  onClick={() => setNotificationType('sms')}
                >
                  <div className="flex items-center justify-center w-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 10.5H16M8 14.5H11M7 19H17C18.1046 19 19 18.1046 19 17V7C19 5.89543 18.1046 5 17 5H7C5.89543 5 5 5.89543 5 7V17C5 18.1046 5.89543 19 7 19Z" />
                    </svg>
                    SMS
                  </div>
                </button>
              </div>
              {notificationType === 'sms' && (
                <div className="mt-3 bg-blue-900/30 border border-blue-600/30 rounded-lg p-4 flex items-start backdrop-blur-sm">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-300">
                    Only customers who have provided consent to receive SMS messages and have a valid phone number will be shown.
                  </p>
                </div>
              )}
            </div>

            {/* Subject field for email */}
            {notificationType === 'email' && (
              <div className="mb-6">
                <label htmlFor="subject" className="block text-md font-medium text-blue-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700/70 text-white border border-gray-600/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-inner"
                  placeholder="Enter email subject..."
                />
              </div>
            )}

            {/* Message Input with character counter */}
            <div className="mb-8">
              <label htmlFor="message" className="block text-md font-medium text-blue-300 mb-2">
                Message
              </label>
              <div className="relative">
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700/70 text-white border border-gray-600/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-inner"
                  rows="4"
                  placeholder="Enter your message here..."
                  maxLength={notificationType === 'sms' ? 160 : undefined}
                ></textarea>
                {notificationType === 'sms' && (
                  <div className="absolute bottom-3 right-3 px-2 py-1 bg-gray-800/80 rounded-md text-xs text-gray-300 backdrop-blur-sm">
                    {160 - message.length} characters left
                  </div>
                )}
              </div>
            </div>

            {/* Customer Selection */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-md font-medium text-blue-300">Select Recipients</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200 px-3 py-1 bg-blue-500/10 rounded-md hover:bg-blue-500/20 border border-blue-500/30"
                  >
                    {selectedCustomers.length === filteredCustomers.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                  {notificationType === 'sms' && (
                    <span className="text-sm text-gray-400 px-2 py-1 bg-gray-700/70 rounded-md border border-gray-600/50">
                      {filteredCustomers.length} eligible
                    </span>
                  )}
                </div>
              </div>
              
              {/* Customer list with gradient scrollbar */}
              <div className="h-60 overflow-y-auto pr-2 bg-gray-800/30 rounded-lg p-4 border border-gray-700/70 custom-scrollbar mb-4">
                {filteredCustomers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filteredCustomers.map((customer) => (
                      <div 
                        key={customer._id} 
                        className={`flex items-center p-3 rounded-lg transition-all duration-200 ${
                          selectedCustomers.includes(customer._id)
                            ? 'bg-blue-600/20 border border-blue-500/40'
                            : 'hover:bg-gray-700/40 border border-gray-700/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={customer._id}
                          checked={selectedCustomers.includes(customer._id)}
                          onChange={() => handleSelectCustomer(customer._id)}
                          className="mr-3 h-4 w-4 text-blue-600 border-gray-500 rounded-sm focus:ring-blue-500 focus:ring-offset-gray-800"
                        />
                        <label htmlFor={customer._id} className="flex-1 cursor-pointer">
                          <div className="text-white font-medium truncate">
                            {customer.name}
                          </div>
                          <div className="text-xs text-gray-400 truncate">
                            {notificationType === 'email' ? customer.email : formatPhoneNumber(customer.phone)}
                          </div>
                        </label>
                        {notificationType === 'sms' && customer.smsConsent && !customer.smsOptOut && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-300 border border-green-700/50">
                            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            SMS
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center py-4 px-6 bg-gray-800/50 rounded-lg border border-gray-700/50">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-gray-400">
                        {notificationType === 'sms' 
                          ? "No customers have opted in for SMS notifications. Please enable SMS consent for customers first."
                          : "No customers found."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Selected count indicator */}
              {filteredCustomers.length > 0 && (
                <div className="flex justify-between items-center text-sm text-gray-400 px-2">
                  <span>{selectedCustomers.length} of {filteredCustomers.length} selected</span>
                  {selectedCustomers.length > 0 && (
                    <span className="text-blue-400">
                      {notificationType === 'sms' 
                        ? `Will send ${selectedCustomers.length} text message${selectedCustomers.length !== 1 ? 's' : ''}`
                        : `Will send to ${selectedCustomers.length} recipient${selectedCustomers.length !== 1 ? 's' : ''}`}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-red-900/30 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            {/* Send Button with gradient */}
            <button
              onClick={handleSendNotification}
              disabled={sending || selectedCustomers.length === 0 || !message.trim() || (notificationType === 'email' && !subject.trim())}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-white flex items-center justify-center transition-all duration-300 ${
                sending || selectedCustomers.length === 0 || !message.trim() || (notificationType === 'email' && !subject.trim())
                  ? 'bg-gray-700 opacity-60 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 shadow-lg hover:shadow-blue-600/25 transform hover:scale-[1.01]'
              }`}
            >
              {sending ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Notifications...
                </div>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send {notificationType === 'sms' ? 'SMS' : 'Email'} {selectedCustomers.length > 0 && `to ${selectedCustomers.length} recipients`}
                </>
              )}
            </button>

            {/* Results Display with improved styling */}
            {result && (
              <div
                className={`mt-8 p-6 rounded-lg border shadow-lg backdrop-blur-sm ${
                  result.success 
                    ? 'bg-green-900/20 border-green-500/30 text-green-200' 
                    : 'bg-red-900/20 border-red-500/30 text-red-200'
                }`}
              >
                <div className="flex items-start">
                  <div className={`rounded-full p-2 mr-4 flex-shrink-0 ${
                    result.success ? 'bg-green-800/50' : 'bg-red-800/50'
                  }`}>
                    {result.success ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-2 ${result.success ? 'text-green-300' : 'text-red-300'}`}>
                      {result.success ? 'Notifications Sent Successfully' : 'Failed to Send Notifications'}
                    </h3>
                    <p className="mb-4">{result.message}</p>
                    
                    {result.details && result.details.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="font-medium">Result Details:</p>
                        <div className={`max-h-60 overflow-y-auto rounded-lg border ${
                          result.success ? 'border-green-700/50' : 'border-red-700/50'
                        }`}>
                          <table className="min-w-full divide-y divide-gray-700">
                            <thead className={result.success ? 'bg-green-900/30' : 'bg-red-900/30'}>
                              <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Recipient</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {result.details.map((item, index) => {
                                const customer = customers.find(c => c._id === item.customerId);
                                return (
                                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/10'}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                      {customer ? customer.name : item.customerId}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        item.status === 'sent' 
                                          ? 'bg-green-100 text-green-800' 
                                          : item.status === 'skipped'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-red-100 text-red-800'
                                      }`}>
                                        {item.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-300">
                                      {item.error || '-'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {result.success && (
                      <button
                        onClick={handleReturnToCustomers}
                        className="mt-6 px-5 py-2 bg-green-600/80 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center backdrop-blur-sm border border-green-500/50"
                      >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Return to Customers
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* API Usage Information with glass morphism */}
            {apiCallInfo && (
              <div className="mt-6 p-6 bg-gray-800/40 rounded-lg border border-gray-700/70 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-900/30 p-2 rounded-full mr-3">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-medium text-blue-300">API Usage Information</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/50">
                    <div className="text-xs text-gray-400 mb-1">Total Recipients</div>
                    <div className="text-xl font-bold text-white">{apiCallInfo.totalRecipients}</div>
                  </div>
                  
                  <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/50">
                    <div className="text-xs text-gray-400 mb-1">API Calls</div>
                    <div className="text-xl font-bold text-white">{apiCallInfo.apiCalls}</div>
                  </div>
                  
                  <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/50">
                    <div className="text-xs text-gray-400 mb-1">Success Rate</div>
                    <div className="text-xl font-bold text-white">
                      {((apiCallInfo.successCount / apiCallInfo.totalRecipients) * 100).toFixed(1)}%
                    </div>
                  </div>
                  
                  <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/50">
                    <div className="text-xs text-gray-400 mb-1">Type</div>
                    <div className="text-xl font-bold text-white capitalize">{apiCallInfo.type}</div>
                  </div>
                </div>
                
                {apiCallInfo.type === 'sms' && (
                  <div className="mt-4 bg-yellow-900/20 border border-yellow-700/40 rounded-lg p-4 text-yellow-300 text-sm flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>
                      Note: SMS messages are sent individually and each counts as one API call against your monthly usage limit.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SendNotifications;

